import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, Typography, TextField, InputAdornment, CircularProgress, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { RootState } from '../../../store';
import { GroupsTeamsService } from '../../../services/groups-teams.service';
import { TeamService } from '../../../services/team.service';
import { TagsService } from '../../../services/tags-service';
import { UserService } from '../../../services/user.service';
import { GT_COLORS, StatCard } from '../../../components/groups-teams/shared';
import { toast } from 'react-toastify';

interface TagAppliedEntity {
    id: string;
    name: string;
    initials: string;
    subtitle: string;
}

interface TagSummary {
    name: string;
    color: string;
    groupCount: number;
    teamCount: number;
    userCount: number;
    totalCount: number;
    groups: TagAppliedEntity[];
    teams: TagAppliedEntity[];
    users: TagAppliedEntity[];
}

const FALLBACK_COLOR = '#637381';

const initialsOf = (...parts: (string | undefined | null)[]) => {
    const s = parts
        .filter(Boolean)
        .map((p) => (p as string).trim()[0])
        .filter(Boolean)
        .join('')
        .toUpperCase();
    return s || '?';
};

const TagPill: React.FC<{ name: string; color: string; selected?: boolean; onClick?: () => void }> = ({
    name,
    color,
    selected,
    onClick,
}) => (
    <Box
        onClick={onClick}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            px: '10px',
            height: 24,
            borderRadius: '12px',
            backgroundColor: `${color}22`,
            color,
            fontSize: 12,
            fontWeight: 600,
            cursor: onClick ? 'pointer' : 'default',
            outline: selected ? `2px solid ${color}` : 'none',
            outlineOffset: '1px',
        }}
    >
        {name}
    </Box>
);

const LIST_PAGE_SIZE = 20;

const CategoryList: React.FC<{
    title: string;
    count: number;
    rows: TagSummary[];
    countOf: (t: TagSummary) => number;
    selectedTag: string | null;
    onSelect: (name: string) => void;
}> = ({ title, count, rows, countOf, selectedTag, onSelect }) => {
    const [page, setPage] = useState(0);
    const totalPages = Math.max(1, Math.ceil(rows.length / LIST_PAGE_SIZE));

    // Reset to first page whenever the filtered row set changes (e.g. search input).
    // Use the array reference rather than length so a same-count-different-content
    // search query also resets the page.
    useEffect(() => {
        setPage(0);
    }, [rows]);

    const start = page * LIST_PAGE_SIZE;
    const visible = rows.slice(start, start + LIST_PAGE_SIZE);

    return (
        <Box
            sx={{
                backgroundColor: GT_COLORS.cardBg,
                border: `1px solid ${GT_COLORS.border}`,
                borderRadius: '10px',
                mb: '12px',
                overflow: 'hidden',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: '14px',
                    py: '10px',
                    backgroundColor: GT_COLORS.headerBg,
                    borderBottom: `1px solid ${GT_COLORS.borderLight}`,
                }}
            >
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: GT_COLORS.textPrimary }}>{title}</Typography>
                <Typography sx={{ fontSize: '13px', fontWeight: 600, color: GT_COLORS.textFaint }}>{count}</Typography>
            </Box>
            {rows.length === 0 ? (
                <Box sx={{ px: '14px', py: '12px' }}>
                    <Typography sx={{ fontSize: '12px', color: GT_COLORS.textFaint }}>No tags applied.</Typography>
                </Box>
            ) : (
                visible.map((t) => {
                    const selected = selectedTag === t.name;
                    return (
                        <Box
                            key={t.name}
                            onClick={() => onSelect(t.name)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: '14px',
                                py: '8px',
                                cursor: 'pointer',
                                backgroundColor: selected ? GT_COLORS.primaryLight : 'transparent',
                                '&:hover': { backgroundColor: selected ? GT_COLORS.primaryLight : GT_COLORS.hoverBg },
                            }}
                        >
                            <TagPill name={t.name} color={t.color} />
                            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: GT_COLORS.textMuted }}>
                                {countOf(t)}
                            </Typography>
                        </Box>
                    );
                })
            )}
            {rows.length > LIST_PAGE_SIZE && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: '14px',
                        py: '6px',
                        borderTop: `1px solid ${GT_COLORS.borderLight}`,
                        backgroundColor: GT_COLORS.headerBg,
                    }}
                >
                    <Typography sx={{ fontSize: '11px', color: GT_COLORS.textFaint }}>
                        {start + 1}–{Math.min(start + LIST_PAGE_SIZE, rows.length)} of {rows.length}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                        <IconButton
                            size="small"
                            disabled={page === 0}
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                        >
                            <KeyboardArrowLeftIcon fontSize="small" />
                        </IconButton>
                        <Typography sx={{ fontSize: '11px', color: GT_COLORS.textMuted, minWidth: 40, textAlign: 'center' }}>
                            {page + 1} / {totalPages}
                        </Typography>
                        <IconButton
                            size="small"
                            disabled={page >= totalPages - 1}
                            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                        >
                            <KeyboardArrowRightIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const AppliedRow: React.FC<{ entity: TagAppliedEntity }> = ({ entity }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            px: '12px',
            py: '8px',
            borderRadius: '8px',
            backgroundColor: GT_COLORS.hoverBg,
            mb: '6px',
        }}
    >
        <Box
            sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: GT_COLORS.primaryLight,
                color: GT_COLORS.primary,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 12,
                fontWeight: 600,
                flexShrink: 0,
            }}
        >
            {entity.initials}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: GT_COLORS.textPrimary }}>
                {entity.name}
            </Typography>
        </Box>
        {entity.subtitle && (
            <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted, ml: 'auto', flexShrink: 0 }}>
                {entity.subtitle}
            </Typography>
        )}
    </Box>
);

export const TagExplorerPage: React.FC = () => {
    const navigate = useNavigate();
    const definedTags = useSelector((s: RootState) => s.tags.items);
    const { l1Groups } = useSelector((s: RootState) => s.groupsTeams);
    const allTeams = useSelector((s: RootState) => s.teams.items);
    const allUsers = useSelector((s: RootState) => s.user.allUsers);
    const tagsLoading = useSelector((s: RootState) => s.tags.loading.fetchAll);
    const teamsLoading = useSelector((s: RootState) => s.teams.loading.fetchAll);
    const usersLoading = useSelector((s: RootState) => s.user.loading.fetchAll);
    const groupsLoading = useSelector((s: RootState) => s.groupsTeams.loading.overview);

    const [search, setSearch] = useState('');
    const [selectedTag, setSelectedTag] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 25;

    useEffect(() => {
        if (definedTags.length === 0) TagsService.fetchAll();
        if (allTeams.length === 0) TeamService.fetchAll();
        if (allUsers.length === 0) UserService.fetchAll();
        if (l1Groups.length === 0) {
            GroupsTeamsService.fetchOverview((ok, msg) => {
                if (!ok) toast.error(msg || 'Failed to load groups.');
            });
        }
    }, []);

    const loading = tagsLoading || teamsLoading || usersLoading || groupsLoading;

    const teamsById = useMemo(() => {
        const map = new Map<string, { name: string; groupId?: string | null }>();
        allTeams.forEach((t) => map.set(t.id, { name: t.name || 'Unnamed team', groupId: t.groupId }));
        return map;
    }, [allTeams]);

    const groupsById = useMemo(() => {
        const map = new Map<string, { name: string }>();
        l1Groups.forEach((g) => map.set(g.id, { name: g.name }));
        return map;
    }, [l1Groups]);

    const summaries = useMemo<Record<string, TagSummary>>(() => {
        // Tags are stored as _id refs on teams/users but pre-resolved to names on l1Groups.
        // Build an id→name map and a name→color map so we can normalize to name as the key.
        const nameById = new Map<string, string>();
        const colorByName = new Map<string, string>();
        definedTags.forEach((t) => {
            if (t._id) nameById.set(t._id, t.name);
            colorByName.set(t.name, t.color || FALLBACK_COLOR);
        });
        // Resolve a raw tag value (id or name) to a known tag name, or null if orphaned.
        const resolve = (raw: string): string | null => {
            if (nameById.has(raw)) return nameById.get(raw)!;
            if (colorByName.has(raw)) return raw;
            return null;
        };

        const acc: Record<string, TagSummary> = {};
        const ensure = (name: string): TagSummary => {
            if (!acc[name]) {
                acc[name] = {
                    name,
                    color: colorByName.get(name) || FALLBACK_COLOR,
                    groupCount: 0,
                    teamCount: 0,
                    userCount: 0,
                    totalCount: 0,
                    groups: [],
                    teams: [],
                    users: [],
                };
            }
            return acc[name];
        };

        // Seed defined tags so unused ones show up with zeros
        definedTags.forEach((t) => ensure(t.name));

        l1Groups.forEach((g) => {
            (g.tags || []).forEach((raw) => {
                const name = resolve(raw);
                if (!name) return;
                const s = ensure(name);
                s.groupCount += 1;
                s.totalCount += 1;
                s.groups.push({
                    id: g.id,
                    name: g.name,
                    initials: initialsOf(g.name),
                    subtitle: g.location || '',
                });
            });
        });

        allTeams.forEach((t) => {
            (t.tags || []).forEach((raw) => {
                const name = resolve(raw);
                if (!name) return;
                const s = ensure(name);
                s.teamCount += 1;
                s.totalCount += 1;
                const groupName = t.groupId ? groupsById.get(t.groupId)?.name : undefined;
                s.teams.push({
                    id: t.id,
                    name: t.name || 'Unnamed team',
                    initials: initialsOf(t.name || ''),
                    subtitle: [t.location, groupName].filter(Boolean).join(' · '),
                });
            });
        });

        allUsers.forEach((u) => {
            (u.tags || []).forEach((raw) => {
                const name = resolve(raw);
                if (!name) return;
                const s = ensure(name);
                s.userCount += 1;
                s.totalCount += 1;
                const team = u.teamId ? teamsById.get(u.teamId) : undefined;
                const groupName = team?.groupId ? groupsById.get(team.groupId)?.name : undefined;
                const fullName = `${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown';
                s.users.push({
                    id: u.id,
                    name: fullName,
                    initials: initialsOf(u.firstName, u.lastName),
                    subtitle: [team?.name, groupName].filter(Boolean).join(' · '),
                });
            });
        });

        return acc;
    }, [definedTags, l1Groups, allTeams, allUsers, teamsById, groupsById]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        const all = Object.values(summaries);
        if (!q) return all;
        return all.filter((s) => s.name.toLowerCase().includes(q));
    }, [summaries, search]);

    const groupTags = useMemo(
        () => filtered.filter((t) => t.groupCount > 0).sort((a, b) => b.groupCount - a.groupCount || a.name.localeCompare(b.name)),
        [filtered]
    );
    const teamTags = useMemo(
        () => filtered.filter((t) => t.teamCount > 0).sort((a, b) => b.teamCount - a.teamCount || a.name.localeCompare(b.name)),
        [filtered]
    );
    const userTags = useMemo(
        () => filtered.filter((t) => t.userCount > 0).sort((a, b) => b.userCount - a.userCount || a.name.localeCompare(b.name)),
        [filtered]
    );
    const unusedTags = useMemo(
        () => filtered.filter((t) => t.totalCount === 0).sort((a, b) => a.name.localeCompare(b.name)),
        [filtered]
    );

    const stats = useMemo(() => {
        const all = Object.values(summaries);
        return {
            group: all.filter((t) => t.groupCount > 0).length,
            team: all.filter((t) => t.teamCount > 0).length,
            user: all.filter((t) => t.userCount > 0).length,
            unused: all.filter((t) => t.totalCount === 0).length,
        };
    }, [summaries]);

    // Drop selection if the tag disappears from the summary set (e.g. data refresh)
    useEffect(() => {
        if (selectedTag && !summaries[selectedTag]) setSelectedTag(null);
    }, [selectedTag, summaries]);

    const handleSelectTag = (name: string) => {
        setSelectedTag(name);
        setPage(0);
    };

    const selected = selectedTag ? summaries[selectedTag] : null;

    // Slice the combined result list across sections so a single Prev/Next
    // walks the whole detail pane (groups → teams → users in order).
    const pageSlices = useMemo(() => {
        if (!selected) return null;
        const start = page * PAGE_SIZE;
        const end = start + PAGE_SIZE;
        let cursor = 0;
        const take = <T,>(rows: T[]): T[] => {
            const lo = Math.max(0, start - cursor);
            const hi = Math.max(0, end - cursor);
            const slice = rows.slice(lo, hi);
            cursor += rows.length;
            return slice;
        };
        return {
            groups: take(selected.groups),
            teams: take(selected.teams),
            users: take(selected.users),
            start,
            end: Math.min(end, selected.totalCount),
            totalPages: Math.max(1, Math.ceil(selected.totalCount / PAGE_SIZE)),
        };
    }, [selected, page]);

    return (
        <Box sx={{ px: { xs: 2, md: 3 } }}>
            {/* Breadcrumb */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', mb: '4px', fontSize: '13px' }}>
                <Typography
                    component="a"
                    onClick={() => navigate('/groups-teams')}
                    sx={{
                        color: GT_COLORS.primary,
                        textDecoration: 'none',
                        fontWeight: 500,
                        cursor: 'pointer',
                        fontSize: '13px',
                        '&:hover': { textDecoration: 'underline' },
                    }}
                >
                    Groups & Teams
                </Typography>
                <Typography sx={{ color: GT_COLORS.textDisabled, fontSize: '11px' }}>›</Typography>
                <Typography sx={{ color: GT_COLORS.textMuted, fontSize: '13px' }}>Tag Explorer</Typography>
            </Box>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: '20px' }}>
                <Box>
                    <Typography sx={{ fontSize: '24px', fontWeight: 600, color: GT_COLORS.textPrimary, lineHeight: 1.2 }}>
                        Tag Explorer
                    </Typography>
                    <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted, mt: '3px' }}>
                        Browse all tags in your organization. Click a tag to see where it's applied.
                    </Typography>
                </Box>
            </Box>

            {/* Search */}
            <TextField
                fullWidth
                placeholder="Search tags..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                size="small"
                sx={{
                    mb: '16px',
                    '& .MuiOutlinedInput-root': {
                        borderRadius: '10px',
                        backgroundColor: GT_COLORS.cardBg,
                    },
                }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ fontSize: 20, color: GT_COLORS.textFaint }} />
                        </InputAdornment>
                    ),
                }}
            />

            {/* Stats */}
            <Box sx={{ display: 'flex', gap: '12px', mb: '20px' }}>
                <StatCard value={stats.group} label="Group tags" />
                <StatCard value={stats.team} label="Team tags" />
                <StatCard value={stats.user} label="User tags" />
                <StatCard value={stats.unused} label="Unused tags" valueColor={stats.unused > 0 ? GT_COLORS.amber : undefined} />
            </Box>

            {/* Body */}
            <Box sx={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                {/* Left: category lists */}
                <Box sx={{ flex: '0 0 360px' }}>
                    <CategoryList
                        title="Group tags"
                        count={groupTags.length}
                        rows={groupTags}
                        countOf={(t) => t.groupCount}
                        selectedTag={selectedTag}
                        onSelect={handleSelectTag}
                    />
                    <CategoryList
                        title="Team tags"
                        count={teamTags.length}
                        rows={teamTags}
                        countOf={(t) => t.teamCount}
                        selectedTag={selectedTag}
                        onSelect={handleSelectTag}
                    />
                    <CategoryList
                        title="User tags"
                        count={userTags.length}
                        rows={userTags}
                        countOf={(t) => t.userCount}
                        selectedTag={selectedTag}
                        onSelect={handleSelectTag}
                    />
                    {unusedTags.length > 0 && (
                        <CategoryList
                            title="Unused tags"
                            count={unusedTags.length}
                            rows={unusedTags}
                            countOf={() => 0}
                            selectedTag={selectedTag}
                            onSelect={handleSelectTag}
                        />
                    )}
                </Box>

                {/* Right: detail pane */}
                <Box
                    sx={{
                        flex: 1,
                        minHeight: 320,
                        backgroundColor: GT_COLORS.cardBg,
                        border: `1px solid ${GT_COLORS.border}`,
                        borderRadius: '10px',
                        p: '20px',
                    }}
                >
                    {loading && Object.keys(summaries).length === 0 ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 320 }}>
                            <CircularProgress size={28} />
                        </Box>
                    ) : !selected ? (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: 320,
                                color: GT_COLORS.textFaint,
                            }}
                        >
                            <LocalOfferOutlinedIcon sx={{ fontSize: 48, mb: '8px' }} />
                            <Typography sx={{ fontSize: '14px', color: GT_COLORS.textMuted }}>
                                Select a tag to see where it's applied.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', mb: '16px' }}>
                                <TagPill name={selected.name} color={selected.color} />
                                <Typography sx={{ fontSize: '13px', color: GT_COLORS.textMuted }}>
                                    {selected.totalCount} total {selected.totalCount === 1 ? 'use' : 'uses'}
                                </Typography>
                            </Box>

                            {pageSlices && pageSlices.groups.length > 0 && (
                                <Box sx={{ mb: '16px' }}>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: GT_COLORS.textMuted, mb: '8px', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                                        Applied to {selected.groups.length} {selected.groups.length === 1 ? 'group' : 'groups'}
                                    </Typography>
                                    {pageSlices.groups.map((e) => (
                                        <AppliedRow key={`g-${e.id}`} entity={e} />
                                    ))}
                                </Box>
                            )}

                            {pageSlices && pageSlices.teams.length > 0 && (
                                <Box sx={{ mb: '16px' }}>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: GT_COLORS.textMuted, mb: '8px', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                                        Applied to {selected.teams.length} {selected.teams.length === 1 ? 'team' : 'teams'}
                                    </Typography>
                                    {pageSlices.teams.map((e) => (
                                        <AppliedRow key={`t-${e.id}`} entity={e} />
                                    ))}
                                </Box>
                            )}

                            {pageSlices && pageSlices.users.length > 0 && (
                                <Box>
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: GT_COLORS.textMuted, mb: '8px', textTransform: 'uppercase', letterSpacing: '.4px' }}>
                                        Applied to {selected.users.length} {selected.users.length === 1 ? 'user' : 'users'}
                                    </Typography>
                                    {pageSlices.users.map((e) => (
                                        <AppliedRow key={`u-${e.id}`} entity={e} />
                                    ))}
                                </Box>
                            )}

                            {selected.totalCount === 0 && (
                                <Typography sx={{ fontSize: '13px', color: GT_COLORS.textFaint, fontStyle: 'italic' }}>
                                    This tag is defined but not applied to any group, team, or user.
                                </Typography>
                            )}

                            {pageSlices && selected.totalCount > PAGE_SIZE && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        gap: '12px',
                                        mt: '16px',
                                        pt: '12px',
                                        borderTop: `1px solid ${GT_COLORS.borderLight}`,
                                    }}
                                >
                                    <Typography sx={{ fontSize: '12px', color: GT_COLORS.textMuted }}>
                                        {pageSlices.start + 1}–{pageSlices.end} of {selected.totalCount}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        disabled={page === 0}
                                        onClick={() => setPage((p) => Math.max(0, p - 1))}
                                    >
                                        <KeyboardArrowLeftIcon fontSize="small" />
                                    </IconButton>
                                    <Typography sx={{ fontSize: '12px', color: GT_COLORS.textMuted, minWidth: 56, textAlign: 'center' }}>
                                        Page {page + 1} of {pageSlices.totalPages}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        disabled={page >= pageSlices.totalPages - 1}
                                        onClick={() => setPage((p) => Math.min(pageSlices.totalPages - 1, p + 1))}
                                    >
                                        <KeyboardArrowRightIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Box>
        </Box>
    );
};
